import React, { useMemo, useState } from "react";

const NO_CATEGORY = "Overige";

function Card({ item, assigned, changed, disabled, noun, meta, onToggle, depth = 0, primary = false, blockedBy = null, onBlocked }) {
  const onDragStart = (e) => {
    e.dataTransfer.setData("text/plain", String(item.id));
    e.dataTransfer.effectAllowed = "move";
  };
  const blocked = !assigned && !!blockedBy;
  const toggle = () => {
    if (blocked) onBlocked(item);
    else onToggle([item.id]);
  };
  return (
    <div
      role="listitem"
      data-testid={`item-${item.id}`}
      draggable={!disabled}
      onDragStart={onDragStart}
      onDoubleClick={() => !disabled && toggle()}
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 shadow-sm ${depth ? "ml-5" : ""} ${
        primary ? "bg-blue-50" : "bg-white"
      } ${blocked ? "opacity-60" : ""} ${disabled ? "" : "cursor-grab active:cursor-grabbing hover:border-blue-300"} ${
        changed ? "border-blue-400 ring-1 ring-blue-200" : primary ? "border-blue-200" : "border-gray-200"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-gray-900">{item.name}</span>
          {primary && (
            <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">basis</span>
          )}
          {changed && (
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-700">
              {assigned ? "nieuw" : "verwijderd"}
            </span>
          )}
        </div>
        {item.description && <div className="mt-0.5 text-xs text-gray-500">{item.description}</div>}
        {meta && <div className="mt-1 text-[11px] text-gray-400">{meta}</div>}
        {blocked && <div className="mt-1 text-[11px] text-amber-700">Vereist eerst {blockedBy}</div>}
      </div>
      {!disabled && (
        <button
          type="button"
          onClick={toggle}
          aria-label={assigned ? `${noun} ${item.name} verwijderen` : `${noun} ${item.name} toekennen`}
          aria-disabled={blocked || undefined}
          title={blocked ? `Ken eerst ${blockedBy} toe` : assigned ? "Verwijderen" : "Toekennen"}
          className={`mt-0.5 rounded border border-gray-200 px-2 py-0.5 text-sm ${
            blocked ? "cursor-not-allowed text-gray-300" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
          }`}
        >
          {assigned ? "→" : "←"}
        </button>
      )}
    </div>
  );
}

/**
 * Twee kolommen: toegekende en beschikbare items (rollen, rechten, ...). Items verplaats je door te slepen, met de
 * pijltjesknop, met een dubbele klik, of per groep met "alles". De verandering gaat via onChange naar de
 * ouder; opslaan doet de ouder.
 *
 * @param {Array} items - alle items {id, name, description, category}
 * @param {number[]} value - toegekende id's
 * @param {number[]} initial - id's zoals ze bewaard zijn (om wijzigingen te markeren)
 * @param {string} noun - enkelvoud voor knoppen ("Rol"), @param {string} plural - meervoud voor titels ("rollen")
 * @param {function} [metaOf] - extra regel per item (ook doorzocht)
 * @param {function} [categoryLabel] - weergavenaam van een categorie
 * @param {boolean} [bulk] - knoppen "alles toekennen/verwijderen" per groep
 * @param {function} [parentOf] - geeft het id van de bovenliggende (primaire) rol; zet de hiërarchie aan: de groepen
 *   zijn dan de primaire rollen met hun onderliggende rollen, een onderliggende rol kan enkel toegekend worden als de
 *   primaire rol er is, en een primaire rol intrekken trekt ook de rollen eronder in
 * @param {function} [isPrimary] - is dit item een primaire rol (bij parentOf)
 */
export default function TransferList({
  items,
  value,
  initial = value,
  onChange,
  disabled = false,
  noun = "Item",
  plural = "items",
  metaOf = () => "",
  categoryLabel = (c) => c,
  bulk = false,
  parentOf = null,
  isPrimary = () => false,
}) {
  const [search, setSearch] = useState("");
  const [over, setOver] = useState(null); // "assigned" | "available"
  const [collapsed, setCollapsed] = useState(() => new Set()); // groepen die ingeklapt zijn (in beide kolommen)
  const [notice, setNotice] = useState("");

  const hierarchy = !!parentOf;
  const assignedSet = useMemo(() => new Set(value), [value]);
  const initialSet = useMemo(() => new Set(initial), [initial]);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const groupKeyOf = (item) => {
    if (!hierarchy) return item.category || NO_CATEGORY;
    if (isPrimary(item)) return `p${item.id}`;
    const parent = parentOf(item);
    return parent && byId.has(parent) ? `p${parent}` : NO_CATEGORY;
  };
  const groupLabelOf = (key) => {
    if (key === NO_CATEGORY) return hierarchy ? "Zonder sectie" : categoryLabel(NO_CATEGORY);
    return hierarchy ? byId.get(Number(key.slice(1)))?.name || key : categoryLabel(key);
  };
  const rank = (item) => (hierarchy && isPrimary(item) ? 0 : 1);

  const groupItems = (list) => {
    const groups = new Map();
    list.forEach((item) => {
      const key = groupKeyOf(item);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return [...groups.entries()]
      .map(([category, groupList]) => ({
        category,
        label: groupLabelOf(category),
        items: groupList.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name, "nl")),
      }))
      .sort((a, b) => (a.category === NO_CATEGORY) - (b.category === NO_CATEGORY) || a.label.localeCompare(b.label, "nl"));
  };

  const query = search.trim().toLowerCase();
  const matches = (item) =>
    !query ||
    item.name.toLowerCase().includes(query) ||
    (item.description || "").toLowerCase().includes(query) ||
    String(metaOf(item) || "").toLowerCase().includes(query) ||
    groupLabelOf(groupKeyOf(item)).toLowerCase().includes(query);

  const assignedItems = items.filter((i) => assignedSet.has(i.id) && matches(i));
  const availableItems = items.filter((i) => !assignedSet.has(i.id) && matches(i));
  const assignedTotal = items.filter((i) => assignedSet.has(i.id)).length;
  const availableTotal = items.length - assignedTotal;

  const allCategories = [...new Set(items.map(groupKeyOf))];
  const allCollapsed = allCategories.length > 0 && allCategories.every((c) => collapsed.has(c));
  const toggleAllGroups = () => setCollapsed(allCollapsed ? new Set() : new Set(allCategories));
  const toggleGroup = (category) =>
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });

  const missingParent = (item, set = assignedSet) => {
    if (!hierarchy) return null;
    const parent = parentOf(item);
    return parent && !set.has(parent) ? byId.get(parent)?.name || "de primaire rol" : null;
  };

  /** Verplaatst items; bij de hiërarchie: onderliggende rollen enkel met hun primaire rol, en een primaire rol
   *  die weggaat neemt de rollen eronder mee. */
  const move = (ids, toAssigned) => {
    const next = new Set(value);
    const blocked = [];
    if (toAssigned) {
      [...ids]
        .sort((a, b) => rank(byId.get(a) || {}) - rank(byId.get(b) || {}))
        .forEach((id) => {
          const item = byId.get(id);
          const missing = item && missingParent(item, next);
          if (missing) blocked.push(missing);
          else next.add(id);
        });
      setNotice(blocked.length ? `Ken eerst ${[...new Set(blocked)].join(", ")} toe: die is vereist.` : "");
    } else {
      let cascaded = false;
      ids.forEach((id) => {
        next.delete(id);
        if (hierarchy && isPrimary(byId.get(id) || {})) {
          items.forEach((i) => {
            if (parentOf(i) === id && next.delete(i.id)) cascaded = true;
          });
        }
      });
      setNotice(cascaded ? "De rollen onder de basisrol zijn ook ingetrokken." : "");
    }
    onChange([...next]);
  };

  const toggle = (ids) => {
    const add = ids.filter((id) => !assignedSet.has(id));
    const remove = ids.filter((id) => assignedSet.has(id));
    if (add.length) move(add, true);
    if (remove.length) move(remove, false);
  };

  const dropOn = (zone) => (e) => {
    e.preventDefault();
    setOver(null);
    if (disabled) return;
    const id = Number(e.dataTransfer.getData("text/plain"));
    if (!items.some((i) => i.id === id)) return;
    const isAssigned = assignedSet.has(id);
    if (zone === "assigned" && !isAssigned) move([id], true);
    if (zone === "available" && isAssigned) move([id], false);
  };

  const zoneProps = (zone) => ({
    onDragOver: (e) => {
      if (disabled) return;
      e.preventDefault();
      if (over !== zone) setOver(zone);
    },
    onDragLeave: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setOver(null);
    },
    onDrop: dropOn(zone),
  });

  const renderGroups = (list, assigned) =>
    groupItems(list).map(({ category, label, items: groupList }) => {
      // tijdens het zoeken blijven alle treffers zichtbaar
      const open = !!query || !collapsed.has(category);
      return (
        <div key={category} className="mb-3 last:mb-0">
          <div className="mb-1 flex items-center justify-between">
            <button
              type="button"
              onClick={() => toggleGroup(category)}
              aria-expanded={open}
              aria-label={`${label} ${open ? "inklappen" : "uitklappen"}`}
              className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600"
            >
              <span aria-hidden="true" className="inline-block w-3">{open ? "▾" : "▸"}</span>
              {label} <span className="font-normal">({groupList.length})</span>
            </button>
            {bulk && !disabled && (
              <button
                type="button"
                onClick={() => move(groupList.map((i) => i.id), !assigned)}
                aria-label={`Alle ${plural} van ${label} ${assigned ? "verwijderen" : "toekennen"}`}
                className="text-[11px] text-blue-600 hover:underline"
              >
                {assigned ? "alles verwijderen →" : "← alles toekennen"}
              </button>
            )}
          </div>
          {open && (
            <div className="space-y-2" role="list">
              {groupList.map((item) => (
                <Card
                  key={item.id}
                  item={item}
                  assigned={assigned}
                  changed={assigned ? !initialSet.has(item.id) : initialSet.has(item.id)}
                  disabled={disabled}
                  noun={noun}
                  meta={metaOf(item)}
                  onToggle={toggle}
                  depth={hierarchy && !isPrimary(item) && parentOf(item) ? 1 : 0}
                  primary={hierarchy && isPrimary(item)}
                  blockedBy={missingParent(item)}
                  onBlocked={(blockedItem) => setNotice(`Ken eerst ${missingParent(blockedItem)} toe: die is vereist.`)}
                />
              ))}
            </div>
          )}
        </div>
      );
    });

  const panel = (zone, title, shown, total, body) => (
    <section
      aria-label={title}
      {...zoneProps(zone)}
      className={`flex min-h-[16rem] flex-col rounded-lg border-2 p-3 transition-colors ${
        over === zone ? "border-blue-400 bg-blue-50" : "border-dashed border-gray-200 bg-gray-50"
      }`}
    >
      <h3 className="mb-3 text-sm font-semibold text-gray-800">
        {title}{" "}
        <span className="font-normal text-gray-400">({query && shown !== total ? `${shown} van ${total}` : total})</span>
      </h3>
      <div className="flex-1">{body}</div>
    </section>
  );

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={toggleAllGroups}
          className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:bg-gray-50"
        >
          {allCollapsed ? "Alles uitklappen" : "Alles inklappen"}
        </button>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Zoek in ${plural}…`}
          aria-label={`Zoek in ${plural}`}
          className="w-64 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {notice && (
        <div role="status" className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {notice}
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {panel(
          "assigned",
          `Toegekende ${plural}`,
          assignedItems.length,
          assignedTotal,
          assignedItems.length ? (
            renderGroups(assignedItems, true)
          ) : (
            <div className="flex h-full items-center justify-center py-8 text-center text-sm text-gray-400">
              {query && assignedTotal ? `Geen ${plural} gevonden` : `Sleep een ${noun.toLowerCase()} hierheen of kies er een met ←`}
            </div>
          )
        )}
        {panel(
          "available",
          `Beschikbare ${plural}`,
          availableItems.length,
          availableTotal,
          availableItems.length ? (
            renderGroups(availableItems, false)
          ) : (
            <div className="flex h-full items-center justify-center py-8 text-center text-sm text-gray-400">
              {query && availableTotal ? `Geen ${plural} gevonden` : `Alle ${plural} zijn toegekend`}
            </div>
          )
        )}
      </div>
    </div>
  );
}
